import { createAsyncThunk } from "@reduxjs/toolkit";

import { CSRF_HEADER, ajax, getCSRFToken } from "../../../utils/ajax";
import * as signals from "../../signals";
import { IBasket, ICustomerSummary } from "../models";

interface IStartAssistingCustomerResult {
    customer: ICustomerSummary;
    basket: IBasket;
}

/**
 * Starts assisting a customer as a CSR agent.
 *
 * This establishes a CSR session where the agent is "assisting" a specific customer.
 * The backend maintains this session state, allowing the CSR to act on behalf of the
 * customer for operations like basket management. After starting assistance, it fetches
 * the customer and basket details to display in the toolbar.
 *
 * Side effects:
 * - Emits `onBeforeStartAssistingCustomer` signal before starting
 * - Emits `onAfterStartAssistingCustomer` signal after completion
 * - These signals can be used by other components to react to assistance state changes
 *
 * @param userURL - The API URL of the customer to assist (e.g., "/api/csr/users/123/")
 * @returns Promise resolving to the customer and their current basket
 *
 * @example
 * dispatch(startAssistingCustomer(customer.url))
 */
export const startAssistingCustomer = createAsyncThunk(
    "csr/startAssistingCustomer",
    async (userURL: string): Promise<IStartAssistingCustomerResult> => {
        signals.csr.onBeforeStartAssistingCustomer.emit();
        await ajax
            .post(`${userURL}begin_assisting/`)
            .set("Accept", "application/json")
            .set(CSRF_HEADER, await getCSRFToken());
        const [customerResp, basketResp] = await Promise.all([
            ajax
                .get("/api/csr/users/currently_assisted_user/")
                .set("Accept", "application/json"),
            ajax
                .get("/api/csr/baskets/currently_assisted_basket/")
                .set("Accept", "application/json"),
        ]);
        signals.csr.onAfterStartAssistingCustomer.emit();
        return {
            customer: customerResp.body as ICustomerSummary,
            basket: basketResp.body as IBasket,
        };
    },
);
